/**
 * Which install guide a device needs (the install / notifications quests,
 * docs/superpowers/specs/2026-10-16-pwa-and-push-quests-design.md).
 *
 * User-agent sniffing, deliberately: there is no feature to detect for "where
 * is the Add to Home Screen button", and the answer differs by browser even on
 * the same OS. Kept pure (all inputs are arguments) so the whole matrix is
 * unit-tested; `currentPlatform()` reads the real values.
 */

export type InstallPlatform =
  | "installed"
  | "ios-safari"
  | "ios-other" // Chrome, Edge, Firefox on iOS: share button in the address bar
  | "ios-inapp" // LinkedIn, Instagram, Facebook, Google app, … — cannot install
  | "android-chromium" // Chrome, Edge, Opera, Brave
  | "android-samsung"
  | "android-firefox"
  | "android-inapp"
  | "desktop-chromium" // Chrome, Edge, Opera, Brave on Windows/macOS/Linux/ChromeOS
  | "macos-safari"
  | "desktop-firefox" // cannot install web apps
  | "unknown";

export interface PlatformInputs {
  userAgent: string;
  /** Launched as an installed app (display-mode: standalone / navigator.standalone). */
  standalone: boolean;
  /** navigator.maxTouchPoints — iPadOS reports a Mac user agent. */
  maxTouchPoints: number;
}

/**
 * In-app browsers of apps people scan QR codes with. They are WebViews: no
 * "Add to Home Screen", and on iOS no push. `; wv)` marks any Android WebView.
 */
const IN_APP =
  /FBAN|FBAV|FB_IAB|Instagram|LinkedInApp|Twitter|Snapchat|Line\/|Slack|MicroMessenger|GSA\/|; wv\)/;

export function detectPlatform({
  userAgent: ua,
  standalone,
  maxTouchPoints,
}: PlatformInputs): InstallPlatform {
  if (standalone) return "installed";
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1);
  if (ios) {
    if (IN_APP.test(ua)) return "ios-inapp";
    if (/CriOS|EdgiOS|FxiOS|OPiOS|OPT\//.test(ua)) return "ios-other";
    return "ios-safari";
  }
  if (/Android/.test(ua)) {
    if (IN_APP.test(ua)) return "android-inapp";
    if (/SamsungBrowser/.test(ua)) return "android-samsung";
    if (/Firefox\//.test(ua)) return "android-firefox";
    return "android-chromium";
  }
  if (/Firefox\//.test(ua)) return "desktop-firefox";
  if (/Chrome\/|Chromium\/|Edg\/|OPR\//.test(ua)) return "desktop-chromium";
  if (/Macintosh/.test(ua) && /Safari\//.test(ua)) return "macos-safari";
  return "unknown";
}

/** iOS (iPhone/iPad), where push exists only inside the installed app. */
export function isIos(platform: InstallPlatform): boolean {
  return platform === "ios-safari" || platform === "ios-other" || platform === "ios-inapp";
}

export function currentPlatform(standalone: boolean): InstallPlatform {
  if (typeof navigator === "undefined") return "unknown";
  return detectPlatform({
    userAgent: navigator.userAgent,
    standalone,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
  });
}
